import { Button, DatePicker, Descriptions, Divider, Flex, Input, List, Select, Spin, Switch } from "antd"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import isEqual from 'lodash/isEqual'
import { DepartmentContext, UpdateDepartmentContext } from "../../../../../context/departmentContext"
import { UpdateUsersContext, UserRequestType, UsersContext } from "../../../../../context/userContext"
import dayjs from "dayjs"
import { CompanyContext, UpdateCompanyContext } from "../../../../../context/companyContext"
import { App } from 'antd'
import { isValidEmail } from "../../../../../utils/validation"
import { buildAdminUserNavigationState, buildDepartmentUserDetailPath, PATH_INFO, resolveAdminUserBackToPath, resolveAdminUserReturnToPath, shouldLockAdminUserDepartment, shouldStopForInvalidContextFetchResult, shouldUseAdminUserHistoryBack } from "../../../../util/helper"
import { useTranslation } from "react-i18next"

const defaultUser: UserRequestType = {
	departmentId: 0,
	position: '',
	name: '',
	email: '',
	start_date: '',
	is_admin: false,
}

export function DepartmentUserManage() {
	const { t } = useTranslation()
	const { refreshDepartmentList } = useContext(UpdateDepartmentContext)
	const { departmentList } = useContext(DepartmentContext)
	const { registerUser, refreshUser, updateUser, reissueUserPinCode, reissueUserPassword } = useContext(UpdateUsersContext)
	const { refreshCompany } = useContext(UpdateCompanyContext)
	const { company } = useContext(CompanyContext)
	const { refreshDepartmentDetail } = useContext(UpdateDepartmentContext)
	const { departmentDetail } = useContext(DepartmentContext)
	const { user } = useContext(UsersContext)
	const { companyId, departmentId, userId } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const backTo = resolveAdminUserBackToPath(location.state, companyId, departmentId)
	const returnTo = resolveAdminUserReturnToPath(location.state, companyId, departmentId, userId)
	const useHistoryBack = shouldUseAdminUserHistoryBack(location.state)
	const shouldUseHistoryBackOnManage = useMemo(
		() => useHistoryBack && returnTo === backTo,
		[backTo, returnTo, useHistoryBack]
	)
	const routeDepartmentId = useMemo(() => {
		if (!departmentId || departmentId === 'undefined') return null
		const parsedDepartmentId = Number(departmentId)
		return Number.isInteger(parsedDepartmentId) && parsedDepartmentId > 0 ? parsedDepartmentId : null
	}, [departmentId])
	const isDepartmentLocked = userId === 'register'
		&& routeDepartmentId !== null
		&& shouldLockAdminUserDepartment(location.state)

	const [userInfo, setUserInfo] = useState<UserRequestType>(defaultUser)
	const [errors, setErrors] = useState<Record<string, boolean>>({})
	const [isLoading, setIsLoading] = useState(false)
	const [isUnchanged, setIsUnchanged] = useState(true)
	const [isPageReady, setIsPageReady] = useState(false)

	const { modal } = App.useApp()

	useEffect(() => {
		let isCancelled = false

		const validateAndLoadUser = async () => {
			setIsPageReady(false)
			if (!companyId || !userId) {
				navigate(PATH_INFO.ERROR.PATH, { replace: true })
				return
			}
			if (userId !== 'register' && routeDepartmentId === null) {
				navigate(PATH_INFO.ERROR.PATH, { replace: true })
				return
			}

			try {
				await refreshDepartmentList(companyId)
				const companyResult = await refreshCompany(companyId)
				if (isCancelled || shouldStopForInvalidContextFetchResult(companyResult, navigate)) return

				if (routeDepartmentId !== null) {
					const departmentResult = await refreshDepartmentDetail(String(routeDepartmentId))
					if (isCancelled || shouldStopForInvalidContextFetchResult(departmentResult, navigate)) return
					if (departmentResult.data?.company?.id !== Number(companyId)) {
						navigate(PATH_INFO.ERROR.PATH, { replace: true })
						return
					}
				}

				if (userId !== 'register') {
					const userResult = await refreshUser(userId)
					if (isCancelled || shouldStopForInvalidContextFetchResult(userResult, navigate)) return
					if (!userResult.data || (routeDepartmentId !== null && userResult.data.department.id !== routeDepartmentId)) {
						navigate(PATH_INFO.ERROR.PATH, { replace: true })
						return
					}
				}

				if (!isCancelled) setIsPageReady(true)
			} catch (error) {
				console.error('Error validating department user manage.', error)
				if (!isCancelled) navigate(PATH_INFO.ERROR.PATH, { replace: true })
			}
		}

		void validateAndLoadUser()
		return () => { isCancelled = true }
	}, [companyId, userId, routeDepartmentId, refreshDepartmentList, refreshCompany, refreshUser, refreshDepartmentDetail, navigate])

	useEffect(() => {
		if (userId === 'register') {
			if (routeDepartmentId !== null) setUserInfo((prev) => ({ ...prev, departmentId: routeDepartmentId }))
			setUserInfo((prev) => ({ ...prev, start_date: dayjs().toString() }))
			return
		} else {
			setUserInfo({
				departmentId: user.department.id || 0,
				position: user.position || '',
				name: user.name,
				email: user.email,
				start_date: user.start_date,
				is_admin: user.is_admin ?? false
			})
		}
	}, [user, userId, routeDepartmentId])

	useEffect(() => {
		if (userId === 'register') {
			setIsUnchanged(false)
			return
		}
		if (!user?.department) {
			setIsUnchanged(true)
			return
		}
		const originalUserInfo = {
			departmentId: user.department.id || 0,
			position: user.position || '',
			name: user.name,
			email: user.email,
			start_date: user.start_date,
			is_admin: user.is_admin ?? false
		}
		setIsUnchanged(isEqual(userInfo, originalUserInfo))
	}, [userInfo, user, userId])

	const inputPlaceHolder = { title: `${company?.company_name}${(departmentId !== 'undefined') ? ` / ${departmentDetail.name}` : ''}` }

	const onClickBack = useCallback(() => {
		if (shouldUseHistoryBackOnManage) {
			navigate(-1)
			return
		}
		const destination = returnTo ?? backTo
		if (destination) {
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}
		navigate(-1)
	}, [backTo, navigate, returnTo, shouldUseHistoryBackOnManage, useHistoryBack])

	const navigateAfterSubmit = useCallback((nextDepartmentId: number) => {
		const targetUserId = userId?.trim()
		const currentUserDetailPath = buildDepartmentUserDetailPath(companyId, departmentId, targetUserId)
		const shouldReturnToUserDetail = Boolean(
			targetUserId
			&& returnTo
			&& currentUserDetailPath
			&& returnTo === currentUserDetailPath
		)

		if (
			targetUserId !== 'register'
			&& shouldReturnToUserDetail
			&& companyId
			&& targetUserId
			&& Number(departmentId) !== nextDepartmentId
		) {
			const destination = buildDepartmentUserDetailPath(companyId, `${nextDepartmentId}`, targetUserId)
			if (!destination) {
				const fallbackDestination = returnTo ?? backTo
				if (fallbackDestination) {
					navigate(fallbackDestination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
					return
				}
				navigate(PATH_INFO.ERROR.PATH, { replace: true })
				return
			}
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}

		if (shouldUseHistoryBackOnManage) {
			navigate(-1)
			return
		}

		const destination = returnTo ?? backTo
		if (destination) {
			navigate(destination, { replace: true, state: buildAdminUserNavigationState(backTo, undefined, useHistoryBack) })
			return
		}

		navigate(-1)
	}, [backTo, companyId, departmentId, navigate, returnTo, shouldUseHistoryBackOnManage, useHistoryBack, userId])

	const onClickSubmit = useCallback(async () => {
		if (!userId) return

		const newErrors: Record<string, boolean> = {};

		// 必須チェック
		['departmentId', 'name', 'email', 'start_date'].forEach((key) => {
			if (!userInfo[key as keyof UserRequestType]) {
				newErrors[key] = true
			}
		})
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			modal.error({
				content: t("messages.confirmInput"),
				centered: true,
			})
			return
		}

		// メールアドレスの形式チェック
		if (!isValidEmail(userInfo.email)) {
			newErrors.email = true
			setErrors(newErrors)
			modal.error({
				content: t("auth.emailInvalid"),
				centered: true,
			})
			return
		}

		setErrors({})
		setIsLoading(true)

		try {
			const submittedUserInfo: UserRequestType = {
				...userInfo,
				departmentId: isDepartmentLocked ? routeDepartmentId : userInfo.departmentId,
				position: userInfo.position || null
			}
			if (userId === 'register') {
				const res = await registerUser(submittedUserInfo)
				if (res[1]) {
					modal.success({ content: res[0], centered: true, onOk: () => navigateAfterSubmit(submittedUserInfo.departmentId) })
				} else {
					modal.error({ content: res[0], centered: true })
				}
			} else {
				// 編集時の更新チェック
				if (isUnchanged) return // isUnchangedがtrueの場合は何もしない

				const res = await updateUser(submittedUserInfo, userId)
				if (res[1]) {
					modal.success({ content: res[0], centered: true, onOk: () => navigateAfterSubmit(submittedUserInfo.departmentId) })
				} else {
					modal.error({ content: res[0], centered: true })
				}
			}
		} catch (error) {
			modal.error({ content: t("messages.processFailed"), centered: true })
		} finally {
			setIsLoading(false)
		}
	}, [registerUser, updateUser, userInfo, userId, modal, navigateAfterSubmit, isUnchanged, routeDepartmentId, isDepartmentLocked, t])

	const onClickReissue = useCallback(async (type: string) => {
		if (!userId) return
		if (userId !== 'register') {
			setIsLoading(true)
			if (type === 'pin') {
				try {
					await reissueUserPinCode(userId)
					modal.success({
						title: t("departmentUser.reissue.pinSuccessTitle"),
						content: t("departmentUser.reissue.pinSuccessContent"),
						centered: true,
					})
				} catch (error) {
					modal.error({
						title: t("departmentUser.reissue.pinErrorTitle"),
						content: t("messages.retry"),
						centered: true,
					})
				} finally {
					setIsLoading(false)
				}
			} else if (type === 'password') {
				try {
					await reissueUserPassword(userId)
					modal.success({
						title: t("departmentUser.reissue.passwordSuccessTitle"),
						content: t("departmentUser.reissue.passwordSuccessContent"),
						centered: true,
					})
				} catch (error) {
					modal.error({
						title: t("departmentUser.reissue.passwordErrorTitle"),
						content: t("messages.retry"),
						centered: true,
					})
				} finally {
					setIsLoading(false)
				}
			}
		}
	}, [userId, reissueUserPinCode, reissueUserPassword, modal, t])

	const dataSource = useMemo(() => {
		const data: {
			key: string;
			label: string;
			value?: string | number | boolean;
			type: string;
			required?: boolean;
			disabled?: boolean;
			onchange?: (value: number | string | boolean | React.ChangeEvent<HTMLInputElement>) => void
		}[] = [
				{
					key: 'departmentName',
					label: t("company.departmentName"),
					value: userInfo.departmentId,
					type: 'select',
					required: true,
					disabled: isDepartmentLocked,
					onchange: (value) => {
						if (typeof value === 'string' || typeof value === 'number') {
							setUserInfo((prev) => ({
								...prev,
								departmentId: typeof value === 'string' ? Number(value) : value
							}))
						}
					}
				},
				{
					key: 'personInCharge',
					label: t("company.position"),
					value: userInfo.position || '',
					type: 'text',
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, position: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'name',
					label: t("company.name"),
					value: userInfo.name,
					type: 'text',
					required: true,
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, name: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'email',
					label: t("company.email"),
					value: userInfo.email,
					type: 'email',
					required: true,
					onchange: (e) => { setUserInfo((prev) => ({ ...prev, email: (e as React.ChangeEvent<HTMLInputElement>).target.value })) }
				},
				{
					key: 'is_admin',
					label: t("departmentUser.adminRole"),
					value: userInfo.is_admin ?? false,
					type: 'switch',
					onchange: (value) => {
						if (typeof value === 'boolean') {
							setUserInfo((prev) => ({ ...prev, is_admin: value }))
						}
					}
				},
				{
					key: 'start_at',
					label: t("company.startDate"),
					value: userInfo.start_date,
					type: 'date',
					required: true,
					onchange: (value) => { setUserInfo((prev) => ({ ...prev, start_date: value as string })) }
				}
			]

		if (userId !== 'register') {
			const pinObj = {
				key: 'pin_code',
				label: t("departmentUser.pin"),
				value: user.pin_code,
				type: 'pin',
				disabled: true
			}
			const passwordObj = {
				key: 'password',
				label: t("auth.password"),
				value: t("departmentUser.maskedPassword"),
				type: 'password',
				disabled: true,
			}
			data.splice(3, 0, pinObj)
			data.splice(4, 0, passwordObj)
		}

		return data
	}, [userInfo, userId, user, isDepartmentLocked, t])

	if (!isPageReady) {
		return (
			<Flex justify="center" align="center" className="min-h-[50vh]">
				<Spin size="large" tip={t("common.loading")} />
			</Flex>
		)
	}

	return (
		<div className="relative">
			<Spin spinning={isLoading} tip={t("common.processing")} size="large" fullscreen />
			<div>
				<div className="flex w-full space-x-5 items-center">
					<p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">{userId === 'register' ? t("departmentUser.registerPage") : t("departmentUser.editPage")}</p>
				</div>
				<Divider />
				<Flex vertical align="center">
					<List
						header={<div className="font-bold break-all">{inputPlaceHolder.title}</div>}
						bordered
						dataSource={dataSource}
						renderItem={(department) => (
							<List.Item style={{ background: '#FFFFFF', justifyContent: 'end' }} >
								<Descriptions column={1} size="small" bordered>
									<Descriptions.Item
										label={(
											<span className="relative inline-block pr-2">
												{department.required && <span className="absolute right-0 top-0 text-red-500 text-[8px] leading-none">※</span>}
												<span>{department.label}</span>
											</span>
										)}
										key={department.key}
									>
										{department.type === 'select' && (
											<Select
												className="w-[320px] text-start"
												options={departmentList.map((el) => {
													return { value: el.id, label: <span>{el.name}</span> }
												})}
												onChange={(value) => department.onchange?.(value)}
												value={userInfo.departmentId || ''}
												disabled={department.disabled}
												status={errors.departmentId ? 'error' : ''}
											/>
										)}
										{(department.type === 'text' || department.type === 'email') && (
											<Input
												type={department.type}
												value={(typeof department.value === 'string' || typeof department.value === 'number') ? department.value : ''}
												disabled={department.disabled}
												onChange={(e) => department.onchange?.(e)}
												className="w-[320px]"
												status={errors[department.key] ? 'error' : ''}
											/>
										)}
										{(department.type === 'pin' || department.type === 'password') && (
											<div className="flex w-[320px]">
												<Input
													type="text"
													value={(typeof department.value === 'string' || typeof department.value === 'number') ? department.value : ''}
													disabled={department.disabled}
													className="mr-2"
												/>
												<Button
													variant="outlined"
													color="primary"
													onClick={() => {
														modal.confirm({
															title: t("departmentUser.reissue.confirmTitle", { target: department.label }),
															content:
																<div className="flex flex-col">
																	<span>{t("departmentUser.reissue.confirmContent", { target: department.label })}</span>
																</div>,
															onOk: async () => {
																await onClickReissue(department.type)
															},
															centered: true,
														})
													}}
												>
													{t("departmentUser.reissue.action")}
												</Button>
											</div>
										)}
										{department.type === 'date' && (
											<DatePicker
												value={(typeof department.value === 'string' || typeof department.value === 'number') ? dayjs(department.value) : undefined}
												onChange={(_, dateString) => {
													if (Array.isArray(dateString)) {
														department.onchange?.(dateString[0])
													} else {
														department.onchange?.(dateString)
													}
												}}
												className="w-[320px]"
												status={errors.start_date ? 'error' : ''}
											/>
										)}
										{department.type === 'switch' && (
											<Flex className="w-[320px]" justify="start">
												<Switch
													checked={Boolean(department.value)}
													onChange={(checked) => department.onchange?.(checked)}
												/>
											</Flex>
										)}
									</Descriptions.Item>
								</Descriptions>
							</List.Item>
						)}
						className="overflow-hidden bg-[#FAFAFA] text-center"
					/>
					<Flex gap="large" className="mt-5">
						<Button variant="outlined" color="primary" size="large" onClick={onClickBack} disabled={isLoading}>{t("common.back")}</Button>
						<Button type="primary" size="large" onClick={onClickSubmit} loading={isLoading} disabled={isLoading || isUnchanged}>{userId === 'register' ? t("common.register") : t("common.update")}</Button>
					</Flex>
				</Flex>
			</div>
		</div>
	)
}
